/**
 * ShortagePRDraftModal — Displays orphan-mark demand shortages and lets
 * the user request a Cognara-drafted Purchase Requisition.
 *
 * Read-only for now: draft can be copied to clipboard or exported as CSV;
 * no procurement submission is performed from this modal.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Loader2, X, AlertTriangle, Sparkles, Copy, Download } from 'lucide-react';

export default function ShortagePRDraftModal({ projectId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [orphans, setOrphans] = useState([]);
  const [summary, setSummary] = useState({ codeCount: 0, totalShortageQty: 0, totalMarksAffected: 0 });
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/swm/allocation/orphan-shortage`);
        setOrphans(data?.orphans || []);
        setSummary({
          codeCount: data?.codeCount || 0,
          totalShortageQty: data?.totalShortageQty || 0,
          totalMarksAffected: data?.totalMarksAffected || 0,
        });
      } catch (e) {
        toast.error('Failed to load shortages: ' + (e?.response?.data?.detail || e.message));
      }
      setLoading(false);
    })();
  }, [projectId]);

  const generateDraft = async () => {
    if (orphans.length === 0) return;
    setDrafting(true);
    setDraft(null);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/swm/allocation/orphan-shortage/cognara-draft`,
        {
          orphans: orphans.map(o => ({
            mCode: o.mCode, description: o.description || '',
            shortageQty: o.shortageQty, unit: o.unit || 'EA',
            marksNeeding: o.marksNeeding || [],
          })),
          urgencyDays: 14,
        },
      );
      setDraft(data);
      toast.success('Cognara draft ready');
    } catch (e) {
      toast.error('Cognara draft failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDrafting(false);
  };

  const copyDraft = () => {
    if (!draft) return;
    navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
    toast.success('PR draft copied to clipboard');
  };

  const exportCsv = () => {
    if (!draft?.lineItems?.length) return;
    const header = ['mCode', 'Spec', 'Qty', 'Unit', 'Vendor Category', 'Lead Time (Days)', 'Justification', 'Risk If Slipped'];
    const rows = draft.lineItems.map(l => [l.mCode, l.spec, l.qty, l.unit, l.vendorCategory, l.leadTimeDays, l.justification, l.riskIfSlipped]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft.prNumber || 'PR-DRAFT'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="shortage-pr-modal">
      <div className="rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,.12))' }}>
        <div className="px-4 py-3 flex items-center gap-3 border-b"
          style={{ background: 'linear-gradient(90deg, #F59E0B 0%, #B45309 100%)', color: '#fff' }}>
          <AlertTriangle className="w-5 h-5" />
          <div className="flex-1">
            <div className="text-sm font-bold">Shortage Companion · Cognara PR Draft</div>
            <div className="text-[10.5px] opacity-90">
              Orphan mark demand with zero MTX supply. Cognara will draft a purchase requisition — no procurement action is taken.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="shortage-pr-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 flex items-center gap-4 border-b text-[11px]" style={{ background: '#FFFBEB' }}>
          <span><b style={{ color: '#B45309' }}>{summary.codeCount}</b> orphan codes</span>
          <span><b style={{ color: '#B45309' }}>{Number(summary.totalShortageQty).toLocaleString()}</b> total shortage qty</span>
          <span><b style={{ color: '#B45309' }}>{summary.totalMarksAffected}</b> marks affected</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={generateDraft} disabled={drafting || loading || orphans.length === 0}
              className="px-3 py-1.5 rounded font-bold text-[11px] text-white flex items-center gap-1.5"
              style={{ background: drafting ? '#94A3B8' : '#B45309' }}
              data-testid="shortage-generate-draft">
              {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {drafting ? 'Cognara drafting…' : (draft ? 'Regenerate' : 'Generate Cognara PR Draft')}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left — orphan table */}
          <div className="border-r overflow-auto" style={{ borderColor: 'rgba(15,23,42,.08)' }}>
            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase" style={{ background: '#F8FAFC', color: '#64748B' }}>
              Orphan Shortages
            </p>
            {loading ? (
              <div className="p-6 flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : orphans.length === 0 ? (
              <div className="p-6 text-[11px]" style={{ color: '#64748B' }}>
                No orphan shortages — every mark BoQ has an MTX supply match.
              </div>
            ) : (
              <table className="w-full text-[10.5px]">
                <thead className="sticky top-0" style={{ background: '#FEF3C7', color: '#92400E' }}>
                  <tr>
                    <th className="p-1.5 text-left uppercase text-[9px]">mCode</th>
                    <th className="p-1.5 text-left uppercase text-[9px]">Description</th>
                    <th className="p-1.5 text-right uppercase text-[9px]">Shortage</th>
                    <th className="p-1.5 text-right uppercase text-[9px]">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((o, i) => (
                    <tr key={o.mCode + i} style={{ borderTop: '1px solid rgba(15,23,42,.06)' }}
                      data-testid={`shortage-row-${o.mCode}`}>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: '#B45309' }}>{o.mCode}</td>
                      <td className="p-1.5 text-[10px]" style={{ color: '#334155' }}>{o.description}</td>
                      <td className="p-1.5 text-right font-mono">{Number(o.shortageQty).toLocaleString()} {o.unit}</td>
                      <td className="p-1.5 text-right font-mono">{o.marksNeeding?.length || o.marksNeedingCount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Right — Cognara draft */}
          <div className="overflow-auto p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase" style={{ color: '#64748B' }}>Cognara PR Draft</p>
              {draft && (
                <div className="flex items-center gap-1">
                  <button onClick={copyDraft} className="px-2 py-1 text-[10px] rounded border flex items-center gap-1"
                    style={{ borderColor: 'rgba(15,23,42,.12)', color: '#475569' }} data-testid="shortage-copy">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <button onClick={exportCsv} className="px-2 py-1 text-[10px] rounded border flex items-center gap-1"
                    style={{ borderColor: 'rgba(15,23,42,.12)', color: '#475569' }} data-testid="shortage-export-csv">
                    <Download className="w-3 h-3" /> CSV
                  </button>
                </div>
              )}
            </div>
            {!draft ? (
              <div className="p-6 text-[11px] rounded" style={{ background: '#F8FAFC', color: '#64748B' }}>
                Click <b style={{ color: '#B45309' }}>Generate Cognara PR Draft</b> to have Cognara compose a
                purchase requisition covering these shortages.
              </div>
            ) : (
              <div className="space-y-3 text-[11px]">
                <div className="p-2 rounded" style={{ background: '#F0F9FF', border: '1px solid #BFDBFE' }}>
                  <div className="text-[9px] uppercase" style={{ color: '#1E4E8C' }}>PR Number</div>
                  <div className="font-mono font-bold text-[12px]" style={{ color: '#1E4E8C' }}>{draft.prNumber}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase mb-1" style={{ color: '#64748B' }}>Summary</div>
                  <p style={{ color: '#334155' }}>{draft.summary}</p>
                </div>
                <div>
                  <div className="text-[9px] uppercase mb-1" style={{ color: '#64748B' }}>Line Items</div>
                  <div className="border rounded overflow-hidden" style={{ borderColor: 'rgba(15,23,42,.08)' }}>
                    <table className="w-full text-[10.5px]">
                      <thead style={{ background: '#F8FAFC' }}>
                        <tr>
                          <th className="p-1.5 text-left uppercase text-[9px]">mCode</th>
                          <th className="p-1.5 text-left uppercase text-[9px]">Spec / Vendor Cat.</th>
                          <th className="p-1.5 text-right uppercase text-[9px]">Qty</th>
                          <th className="p-1.5 text-right uppercase text-[9px]">Lead (d)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(draft.lineItems || []).map((l, i) => (
                          <tr key={i} style={{ borderTop: '1px solid rgba(15,23,42,.06)' }}>
                            <td className="p-1.5 font-mono font-semibold" style={{ color: '#B45309' }}>{l.mCode}</td>
                            <td className="p-1.5">
                              <div style={{ color: '#334155' }}>{l.spec}</div>
                              <div className="text-[9px]" style={{ color: '#64748B' }}>{l.vendorCategory}</div>
                            </td>
                            <td className="p-1.5 text-right font-mono">{Number(l.qty || 0).toLocaleString()} {l.unit}</td>
                            <td className="p-1.5 text-right font-mono">{l.leadTimeDays}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {draft.delivery && (
                  <div className="p-2 rounded" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                    <div className="text-[9px] uppercase" style={{ color: '#166534' }}>Delivery</div>
                    <div style={{ color: '#166534' }}>
                      {draft.delivery.targetDate} · {draft.delivery.shipTo}
                    </div>
                  </div>
                )}
                {draft._source === 'fallback' && (
                  <div className="text-[9px] italic" style={{ color: '#94A3B8' }}>
                    Cognara AI unavailable — deterministic fallback draft. Retry to regenerate via AI.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
